import { useState } from 'react';
import type { ClientDeletionStep, ManualRemoval } from '@hangar/shared';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { deletionStepView, deletionSummary, safeLink, stepLabel } from './clientLifecycleModel';

const TONE_TEXT: Record<string, string> = { succeeded: 'text-succeeded', failed: 'text-failed', queued: 'text-muted-foreground' };

interface Props {
  steps: ClientDeletionStep[];
  /** Runs the deletion again from the failed step; absent once nothing can be retried. */
  onRetry?: () => void;
  retrying?: boolean;
}

/** Each deletion step with its status, the provider resources to remove by hand as a checklist, and Retry on failure. */
export function DeletionProgress({ steps, onRetry, retrying }: Props) {
  const summary = deletionSummary(steps);
  return (
    <div data-testid="client-deletion" data-state={summary.state} className="flex min-w-0 flex-col gap-4">
      <p role="status" aria-live="polite" data-testid="client-deletion-headline" className={cn('flex items-start gap-1.5 text-[13px] font-medium', summary.state === 'failed' ? 'text-failed' : summary.state === 'done' ? 'text-succeeded' : 'text-running')}>
        <Icon name={summary.state === 'failed' ? 'error' : summary.state === 'done' ? 'check_circle' : 'progress_activity'} size={16} className="mt-px" />
        {summary.headline}
      </p>
      <ol className="flex flex-col gap-2" aria-label="Deletion steps">
        {steps.map((s) => <StepRow key={s.key} step={s} />)}
      </ol>
      {summary.manual.length ? <ManualChecklist items={summary.manual} /> : null}
      {summary.state === 'failed' && onRetry ? (
        <Button data-testid="client-deletion-retry" variant="destructive" className="w-fit max-sm:h-11 max-sm:w-full" disabled={retrying} onClick={onRetry}>
          <Icon name="refresh" size={18} />
          {retrying ? 'Retrying…' : 'Retry deletion'}
        </Button>
      ) : null}
    </div>
  );
}

function StepRow({ step }: { step: ClientDeletionStep }) {
  const view = deletionStepView(step);
  return (
    <li data-testid={`client-deletion-step-${step.key}`} data-status={step.status} className="flex min-w-0 items-start gap-2.5 rounded-lg border px-3 py-2.5">
      <Icon name={view.icon} size={18} className={cn('mt-px shrink-0', TONE_TEXT[view.tone])} />
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-foreground">{stepLabel(step.key)}</div>
        <div className={cn('break-words text-xs', TONE_TEXT[view.tone])}>{view.text}</div>
      </div>
    </li>
  );
}

/** What the providers do not let Hangar remove: ticked off by the operator as they go (kept for this page only). */
function ManualChecklist({ items }: { items: ManualRemoval[] }) {
  const [done, setDone] = useState<Set<number>>(new Set());
  const toggle = (i: number, on: boolean) => setDone((d) => { const next = new Set(d); if (on) next.add(i); else next.delete(i); return next; });
  return (
    <section data-testid="client-deletion-manual" aria-labelledby="client-deletion-manual-title" className="flex flex-col gap-2 rounded-lg border border-awaiting/30 bg-awaiting/5 p-4">
      <Kicker className="text-[11px] text-awaiting">
        <span id="client-deletion-manual-title">Remove by hand · {done.size} of {items.length}</span>
      </Kicker>
      <p className="text-xs text-muted-foreground">The provider has no API Hangar may use for these: remove each one where shown, then tick it.</p>
      <ul className="flex flex-col gap-1">
        {items.map((item, i) => <ManualItem key={`${item.what}-${i}`} item={item} index={i} checked={done.has(i)} onChange={(on) => toggle(i, on)} />)}
      </ul>
    </section>
  );
}

function ManualItem({ item, index, checked, onChange }: { item: ManualRemoval; index: number; checked: boolean; onChange: (on: boolean) => void }) {
  const href = safeLink(item.where);
  const id = `client-manual-${index}`;
  return (
    <li data-testid={`client-deletion-manual-${index}`} className="flex min-h-11 items-start gap-2.5 py-1.5">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <Label htmlFor={id} className={cn('cursor-pointer text-[13px] font-medium leading-snug', checked && 'text-muted-foreground line-through')}>{item.what}</Label>
        {href ? (
          <a href={href} target="_blank" rel="noreferrer noopener" className="mt-0.5 flex items-center gap-1 break-all text-xs text-primary underline-offset-2 hover:underline">
            {item.where}
            <Icon name="open_in_new" size={14} />
          </a>
        ) : (
          <p className="mt-0.5 break-words text-xs text-muted-foreground">{item.where}</p>
        )}
      </div>
    </li>
  );
}
